import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Clock,
  Users,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const CoursesPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    courseName: "",
    courseCode: "",
    description: "",
    durationMonths: "",
    feeAmount: "",
  });
  const [viewingCourse, setViewingCourse] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/course/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        // Map response to match UI fields
        const mappedCourses = data.data.map((course) => ({
          ...course,
          totalStudents: course.totalStudents || 0,
          totalBatches: course.totalBatches || 0,
          isActive: true, // API doesn't return this yet for listing, default to true
        }));
        setCourses(mappedCourses);
      }
    } catch (error) {
      toast.error("Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    try {
      const payload = {
        ...formData,
        durationMonths: Number(formData.durationMonths),
        feeAmount: Number(formData.feeAmount),
      };

      if (editingCourse) {
        await axios.put(`${server}/course/updateById`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            courseId: editingCourse.id,
          },
        });
        toast.success("Course updated successfully!");
      } else {
        await axios.post(`${server}/course/new`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        toast.success("Course created successfully!");
      }

      setShowCreateModal(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(
        editingCourse ? "Failed to update course" : "Failed to create course"
      );
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) {
      return;
    }

    try {
      await axios.delete(`${server}/course/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          courseId: courseId,
        },
      });
      toast.success("Course deleted successfully");
      fetchCourses();
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  const resetForm = () => {
    setFormData({
      courseName: "",
      courseCode: "",
      description: "",
      durationMonths: "",
      feeAmount: "",
    });
    setEditingCourse(null);
  };

  const filteredCourses = courses.filter((course) =>
    `${course.courseName} ${course.courseCode}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              Courses Management
            </h1>
            <p className="text-gray-400 text-lg">
              Manage all courses and curriculum
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg hover:shadow-purple-500/20 transition-all font-semibold"
          >
            <Plus className="w-5 h-5" />
            Create Course
          </button>
        </div>

        {/* Search */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search courses by name or code..."
              className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-20 bg-gray-800 rounded-xl border border-gray-700">
            <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No courses found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create First Course
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                onEdit={(course) => {
                  setEditingCourse(course);
                  setFormData({
                    courseName: course.courseName,
                    courseCode: course.courseCode,
                    description: course.description,
                    durationMonths: course.durationMonths,
                    feeAmount: course.feeAmount,
                  });
                  setShowCreateModal(true);
                }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CourseModal
          formData={formData}
          setFormData={setFormData}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          onSave={handleSaveCourse}
          isEditing={!!editingCourse}
        />
      )}

      {viewingCourse && (
        <CourseDetailsModal
          course={viewingCourse}
          onClose={() => setViewingCourse(null)}
        />
      )}
    </div>
  );
};

// Course Card Component
const CourseCard = ({ course, onEdit, onDelete, onView }) => {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-purple-500 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white mb-2">
            {course.courseName}
          </h3>
          <p className="text-purple-400 text-sm font-medium mb-3">
            {course.courseCode}
          </p>
          <p className="text-gray-400 text-sm line-clamp-2">
            {course.description}
          </p>
        </div>
        {course.isActive && (
          <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
            Active
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-900 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400 text-xs">Duration</span>
          </div>
          <p className="text-white font-semibold">
            {course.durationMonths} months
          </p>
        </div>
        <div className="bg-gray-900 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-green-400" />
            <span className="text-gray-400 text-xs">Fee</span>
          </div>
          <p className="text-white font-semibold">
            ₹{course.feeAmount.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-900 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-gray-400 text-xs">Students</span>
          </div>
          <p className="text-white font-semibold">{course.totalStudents}</p>
        </div>
        <div className="bg-gray-900 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-orange-400" />
            <span className="text-gray-400 text-xs">Batches</span>
          </div>
          <p className="text-white font-semibold">{course.totalBatches}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onView}
          className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onEdit(course)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => onDelete(course.id)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Create/Edit Modal Component
const CourseModal = ({ formData, setFormData, onClose, onSave, isEditing }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-white mb-6">
          {isEditing ? "Edit Course" : "Create New Course"}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Course Name *
            </label>
            <input
              type="text"
              value={formData.courseName}
              onChange={(e) =>
                setFormData({ ...formData, courseName: e.target.value })
              }
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              placeholder="e.g., JEE Main Preparation"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Course Code *
            </label>
            <input
              type="text"
              value={formData.courseCode}
              onChange={(e) =>
                setFormData({ ...formData, courseCode: e.target.value })
              }
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              placeholder="e.g., JEE-MAIN-2024"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
              placeholder="Brief description of the course..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Duration (Months) *
              </label>
              <input
                type="number"
                value={formData.durationMonths}
                onChange={(e) =>
                  setFormData({ ...formData, durationMonths: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="12"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Fee Amount (₹) *
              </label>
              <input
                type="number"
                value={formData.feeAmount}
                onChange={(e) =>
                  setFormData({ ...formData, feeAmount: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="50000"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="flex-1 px-6 py-3 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
          >
            {isEditing ? "Update Course" : "Create Course"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const CourseDetailsModal = ({ course, onClose }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-2xl w-full border border-gray-700 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✖
        </button>

        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-purple-400" />
          Course Details
        </h2>

        <div className="space-y-6">
          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {course.courseName}
                </h3>
                <p className="text-purple-400 font-medium">
                  {course.courseCode}
                </p>
              </div>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                Active
              </span>
            </div>
            <p className="text-gray-400 mt-2">
              {course.description || "No description available."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-gray-400 text-sm">Duration</span>
              </div>
              <p className="text-white font-semibold text-lg">
                {course.durationMonths} Months
              </p>
            </div>
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-gray-400 text-sm">Course Fee</span>
              </div>
              <p className="text-white font-semibold text-lg">
                ₹{course.feeAmount?.toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-purple-400" />
                <span className="text-gray-400 text-sm">Total Students</span>
              </div>
              <p className="text-white font-semibold text-lg">
                {course.totalStudents}
              </p>
            </div>
            <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-orange-400" />
                <span className="text-gray-400 text-sm">Total Batches</span>
              </div>
              <p className="text-white font-semibold text-lg">
                {course.totalBatches}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CoursesPage;
